/**
 * @fileoverview Own the outer application's credentialed HTTP transport and environment-selected backend root. Token refresh/retry and `fileAdmissionTransport.js` (polling and replay of uploads pending file security checks) are shared infrastructure; callers own request identity and business-state reconciliation. `browserErrorReporting.js` receives bounded failure observations. A transport retry and a safely repeatable business action remain different responsibilities; backend authorization still governs each request.
 */

import axios from 'axios';
import { reportBrowserError } from './browserErrorReporting';
import { installFileAdmissionTransport } from './fileAdmissionTransport';

const developmentEndpoint = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5100/api';
const productionEndpoint = process.env.REACT_APP_API_BASE_URL || '/api';



const getEndpoint = () => {
    if (process.env.NODE_ENV === 'development') {
        return developmentEndpoint;
    } else {
        return productionEndpoint;
    }
};

const api = axios.create({
    baseURL: getEndpoint(),
    withCredentials: true, // Add other common configurations if needed
});

installFileAdmissionTransport(api);

api.interceptors.response.use(
    response => {
        return response;
    },


    async error => {
        const originalRequest = error.config;

        if (
            error.response &&
            error.response.status === 401 &&
            error.response.data &&
            error.response.data.msg === "Token has expired" &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;

            try {
                // Attempt refresh token via cookie-based session; backend resets cookie
                console.log('Attempting token refresh...');
                await api.post('/refresh_token');
                console.log('Refresh token successful. Retrying original request.');

                // Retry original request; cookies now contain fresh JWT
                return api(originalRequest);
            } catch (_error) {
                console.error('Failed to refresh token:', _error.response?.status ?? 'network error');
            }
        }

        if (originalRequest?.url?.startsWith('/profile/phone-settings')) {
            // Axios errors retain request bodies; never log personal phone PINs.
            console.error('Phone settings request failed:', error.response?.status ?? 'network error');
        } else {
            console.error('Request error:', error.response?.status ?? 'network error');
        }

        if (error.code !== 'ERR_CANCELED' && (!error.response || error.response.status >= 500)) {
            reportBrowserError(error.response ? 'http' : 'network', error, {
                status_code: error.response?.status,
                correlation_id: error.response?.headers?.['x-correlation-id'],
                api_route: error.response?.headers?.['x-route-template'],
            });
        }

        return Promise.reject(error);
    }
);

export default api;
